import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import url from 'url';

import DiscoverAlbumFolderStartGeneric from './start/DiscoverAlbumFolderStartGeneric';
import { ALBUM_SUBTYPE, ALBUM_TYPE } from '../../constants/albums';
import DiscoverAlbumFolderStartCamps from './start/DiscoverAlbumFolderStartCamps';
import Spinner from '../global/Spinner';
import DiscoverAlbumFolderStartSchools from './start/DiscoverAlbumFolderStartSchools';
import DiscoverAlbumFolderStartWeddings from './start/DiscoverAlbumFolderStartWeddings';
import { sentryCaptureMessage } from '../../util/sentry';
import OnboardingLandingSchoolsHeadMeta from '../onboarding/start/OnboardingLandingSchoolsHeadMeta';
import OnboardingLandingCampsHeadMeta from '../onboarding/start/OnboardingLandingCampsHeadMeta';
import AlbumFolderStartShutterbug from '../../apps/temp/views/onboarding/AlbumFolderStart/components/AlbumFolderStartShutterbug';
import DiscoverAlbumFolderStartSports from './start/DiscoverAlbumFolderStartSports';
import OnboardingLandingSportsHeadMeta from '../onboarding/start/OnboardingLandingSportsHeadMeta';
import DiscoverAlbumFolderStartChurch from './start/DiscoverAlbumFolderStartChurch';
import OnboardingLandingChurchHeadMeta from '../onboarding/start/OnboardingLandingChurchHeadMeta';
import { isWeddingsContent } from '../../util/album';

class DiscoverAlbumFolderStart extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      album_type: PropTypes.string,
      album_subtype: PropTypes.string,
      is_demo: PropTypes.bool,
      has_charity: PropTypes.bool,
      charity_name: PropTypes.string,
      charity_percentage: PropTypes.number,
      account: PropTypes.shape({
        name: PropTypes.string,
        logo_image: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
      custom_onboarding_url: PropTypes.string,
    }),
    authenticated: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
    loading: PropTypes.bool,
  };

  // If this page is accessed with a `joinType` query it means
  // we're coming in from Unbounce so we need to skip this page entirely.
  // TODO: Temporary measure
  componentDidMount() {
    const { albumFolder, location } = this.props;

    if (!location?.search && !albumFolder) {
      return;
    }

    const { joinType } = qs.parse(location?.search ?? '');

    if (joinType) {
      this.onGetStarted();
    }

    if (
      [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT].includes(
        albumFolder?.album_type,
      ) ||
      [ALBUM_SUBTYPE.EVENTS, ALBUM_SUBTYPE.WEDDINGS].includes(
        albumFolder?.album_subtype,
      )
    ) {
      this.onGetStarted();
    }
  }

  componentDidUpdate() {
    const { loading, albumFolder } = this.props;

    // If there is a custom url set on the album folder, redirect to it.
    if (albumFolder?.custom_onboarding_url) {
      // Add the folder id so that unbounce knows how to get back.
      // eg unbounce could then make a '/discover/album-folder/{album-folder-id}/faces' link.
      window.location.assign(
        `${albumFolder.custom_onboarding_url}?album_folder=${albumFolder.id}`,
      );
    }

    if (!loading && !albumFolder) {
      sentryCaptureMessage('[DiscoverAlbumFolderStart] - No album folder', {
        level: 'warning',
        extra: {
          ...this.props,
        },
      });
    }

    if (!albumFolder) {
      return;
    }

    // As per WAL-6143, we want to skip this page.
    if (
      [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT].includes(
        albumFolder?.album_type,
      ) ||
      [ALBUM_SUBTYPE.EVENTS, ALBUM_SUBTYPE.WEDDINGS].includes(
        albumFolder?.album_subtype,
      )
    ) {
      this.onGetStarted();
    }
  }

  onGetStarted = () => {
    const { albumFolder, authenticated, history, location } = this.props;
    const targetUrl = url.format({
      pathname: `/discover/album-folder/${albumFolder.id}`,
      search: location?.search ?? '',
    });

    const currentSearch = location?.search ?? '';
    const currentPath = location?.pathname ?? '';

    // Check if the URL is from a QR code scan (path format and utm_medium=qr-code)
    const isFromQrCode =
      /^\/discover\/album-folder\/[^/]+\/start$/.test(currentPath) &&
      (currentSearch.includes('utm_medium=qr-code') ||
        qs.parse(currentSearch).next?.includes('utm_medium=qr-code'));

    if (albumFolder?.is_web_gallery && albumFolder?.joins_enabled) {
      if (isFromQrCode) {
        // For QR code scans, redirect to the gallery without query params
        const firstAlbumId = albumFolder?.children?.albums?.records?.[0]?.id;

        if (firstAlbumId) {
          history.replace({
            pathname: `/galleries/view/album/${firstAlbumId}`,
            search: '',
          });
        } else {
          // should not happen but in the odd case we don't have an album - fallback to folder view if no albums exist
          history.replace({
            pathname: `/galleries/view/folder/${albumFolder.id}`,
            search: '',
          });
        }

        return;
      }
    }

    if (authenticated) {
      history.replace(targetUrl);
    } else {
      history.replace({
        pathname: '/auth/onboarding',
        search: `?${qs.stringify({
          albumFolderId: albumFolder.id,
          next: targetUrl,
        })}`,
      });
    }
  };

  render() {
    const { albumFolder, loading } = this.props;

    if (loading || !albumFolder) {
      return (
        <div className="text-center mt-5">
          <Spinner />
        </div>
      );
    }

    if (isWeddingsContent(albumFolder)) {
      return (
        <DiscoverAlbumFolderStartWeddings
          albumFolder={albumFolder}
          onGetStarted={this.onGetStarted}
        />
      );
    }

    if (
      albumFolder.album_type === ALBUM_TYPE.SHUTTERBUG ||
      albumFolder.album_subtype === ALBUM_SUBTYPE.GREEK ||
      albumFolder.album_type === ALBUM_TYPE.EVENT
    ) {
      return (
        <AlbumFolderStartShutterbug
          albumFolder={albumFolder}
          onGetStarted={this.onGetStarted}
        />
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.CAMPS) {
      return (
        <Fragment>
          <OnboardingLandingCampsHeadMeta />
          <DiscoverAlbumFolderStartCamps
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.SCHOOLS) {
      return (
        <Fragment>
          <OnboardingLandingSchoolsHeadMeta />
          <DiscoverAlbumFolderStartSchools
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.SPORTS) {
      return (
        <Fragment>
          <OnboardingLandingSportsHeadMeta />
          <DiscoverAlbumFolderStartSports
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    if (albumFolder.album_subtype === ALBUM_SUBTYPE.CHURCH) {
      return (
        <Fragment>
          <OnboardingLandingChurchHeadMeta />
          <DiscoverAlbumFolderStartChurch
            albumFolder={albumFolder}
            onGetStarted={this.onGetStarted}
          />
        </Fragment>
      );
    }

    return (
      <DiscoverAlbumFolderStartGeneric
        albumFolder={albumFolder}
        onGetStarted={this.onGetStarted}
      />
    );
  }
}

export default DiscoverAlbumFolderStart;
